// Selected-account context. The user can hold several broker accounts (T212 demo,
// T212 live, IBKR …); this picks the ACTIVE one and exposes it as a query string
// the pages append to every /api/account* call so demo and live data never
// conflate. `live` drives the unmistakable real-money warnings. Persisted.
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "./useApi.js";
import { setAccountCurrency } from "./currency.js";

const Ctx = createContext({
  accounts: [], id: null, select: () => {}, broker: "T212", env: "demo",
  live: false, mode: "MANUAL", qs: "broker=T212&env=demo", label: "T212 demo", ready: false,
});
const KEY = "cc-account";

export function AccountProvider({ children }) {
  const { data } = useApi("account");                 // { accounts:[{id,broker,env,live,label}], default_account }
  const accounts = data?.accounts || [];
  const [, setSearchParams] = useSearchParams();
  const [stored, setStored] = useState(() => { try { return localStorage.getItem(KEY); } catch { return null; } });
  const id = useMemo(() => {
    const ids = accounts.map((a) => a.id);
    // Trust the persisted choice BEFORE the account list loads (ids empty) — the stored id already
    // encodes broker:env, so `qs` is correct from the first render. Without this, the first render
    // falls back to T212:demo and then flips when /api/account resolves a few seconds later, which
    // re-fires every ?qs fetch and flashes the wrong account's data (the "page re-renders" report).
    if (stored && (ids.length === 0 || ids.includes(stored))) return stored;
    return data?.default_account || ids[0] || null;        // else server default (prefers T212 demo)
  }, [accounts, stored, data?.default_account]);
  const select = useCallback((newId) => {
    setStored(newId);
    try { localStorage.setItem(KEY, newId); } catch {}
    // Wrong-account-order safety: a trade/stock modal open in the URL (?trade= / ?stock=) stays
    // MOUNTED across an account switch — broker/env/gating flip to the new account while the modal's
    // size + `account` prop stay stale, so a placed order could hit the wrong broker at a stale size.
    // Close any open modal on switch (strip the params, in place) so the user re-opens against the
    // now-current account. No-op when nothing is open.
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      if (!n.has("trade") && !n.has("stock")) return prev;   // nothing open → don't touch history
      n.delete("trade"); n.delete("stock");
      return n;
    }, { replace: true });
  }, [setSearchParams]);
  const acct = accounts.find((a) => a.id === id) || null;
  const [broker, env] = id ? id.split(":") : ["T212", "demo"];
  const live = env === "live";
  // SINGLE SOURCE for live-money labelling: every trade confirm/toast/modal/page reads this. The
  // DISPLAY override wins (the user may view a EUR account in USD); else the selected account's own
  // currency. Using the native currency here was the bug where the display-currency picker had no
  // effect on pages that don't re-set it (e.g. Trading) — the symbol stayed native while values were
  // converted, so "€" labelled USD-converted numbers.
  setAccountCurrency(data?.display_currency || acct?.currency);
  // Stabilize the context value's identity so consumers don't re-render on every provider render
  // (fresh object each render = app-wide re-render storm). Contents are unchanged.
  const value = useMemo(() => ({
    accounts, id, select, broker, env, live,
    mode: acct?.mode || data?.mode || "MANUAL",     // D3: the SELECTED account's execution mode
    displayMode: data?.currency_display_mode || "unified",   // unified | native currency display (PORTFOLIO_SSOT)
    qs: `broker=${broker}&env=${env}`,
    label: acct?.label || `${broker} ${env}`, ready: !!data,
  }), [accounts, id, broker, env, live, acct, data]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAccount = () => useContext(Ctx);

// One source of truth for "this account executes automatically" (Helm keeps the demo book deployed
// on a Deploy): T212 Auto OR any IBKR account. Used by Today AND the execution-mode banner so the
// two surfaces never give the set-and-forget user contradictory instructions.
export const isAutoMode = (m) => m === "T212_AUTO" || m === "IBKR";
